import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { Printer, AlertCircle, AlertTriangle, Loader2, Check, CloudOff, RotateCcw, BookOpen, ChevronDown, ChevronRight } from 'lucide-react'
import { useApp } from '../App'
import { menusAPI, orderingAPI, formatQuantity, convertFromLbs, getUnitLabel } from '../lib/api'
import { useWeightsStore } from '../lib/useWeightsStore'
import {
  MEAL_CATEGORIES, SIDE_CATEGORIES, WEIGHED_TYPES, TYPE_CONFIG, OZ_PER_LB,
  buildIngredientGroups, groupedOrderingLookup, fmtPieces, groupedInput, groupedSaveState, round1, resolveCurrentWeekMenu, setGroupedCell, isCheckOnly,
} from '../lib/plating'
import { trIngredient, trLabel } from '../lib/translations'
import RecipeSheetModal from '../components/RecipeSheetModal'

// ══════════════════════════════════════════════════════════════════════════════
// PREP — cut it, tick it, weigh it
// ══════════════════════════════════════════════════════════════════════════════
//
// Built for a lead prep cook on an iPad propped on a counter, gloves on, not for
// the person who built the menu. One screen, worked straight down: how much to
// cut, a checkbox, and the weight entered in the same place. The sticky roll-up
// answers "is everything cut and weighed, and what's outstanding?" at a glance —
// so whatever isn't done by the end of Friday is simply what's left on Saturday.
//
// Two numbers with different jobs, deliberately kept apart:
//   "Cut this much"  — the Ordering tab's Needed (w/ Buffer), raw, the instruction
//   "Recipe needs"   — the Weights tab's cooked figure, what the short-check uses
//
// Everything persists to the same `weight_entries` store the Weights tab uses, so
// a weight entered here is the same record there, and vice versa.

// Fire the short-weight alert when the cooked weight comes in more than 30%
// under what the recipes need. One threshold — no urgent/warning tiers.
const SHORT_THRESHOLD = 0.7

// A finished row drops to the bottom of its group so the outstanding work stays
// at the top, where the cook is looking. It waits this long after the last edit
// before moving: re-sorting on each keystroke would yank the row out from under
// their finger mid-entry, and shift the next row up just as they reach for it.
const SINK_DELAY_MS = 700

export default function Prep({ typeFilter = null, ingredientFilter = null, layout = 'list' }) {
  const { currentCity, currentMenu, menus, cities, recipes, isSpanish, translations } = useApp()

  // A prep cook must never silently work last week's list because localStorage
  // cached it, and must never be asked to pick a menu — resolve today's by week_of.
  const prepMenuId = currentMenu

  const [menu, setMenu] = useState(null)
  const [needed, setNeeded] = useState(null)      // ingKey -> Ordering row (null until loaded)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)

  const [shortAlert, setShortAlert] = useState(null)   // { key, name, need, weighed, unit }
  const [recipeSheet, setRecipeSheet] = useState(null) // { recipeId, name }
  const warnedRef = useRef(new Set())                  // don't re-nag per keystroke
  const weightInputs = useRef({})                      // key -> input el, for "Re-enter"
  const [selectedKey, setSelectedKey] = useState(null)
  const [doneOpen, setDoneOpen] = useState(false)

  const store = useWeightsStore(currentCity, prepMenuId)
  const { inputs, setCell, stateFor, clearAll, retryFailed, failedCount } = store

  const t = useCallback((english) => trLabel(isSpanish, translations, english), [isSpanish, translations])
  const ingredientName = useCallback((ingredient) => (
    trIngredient(isSpanish, translations, ingredient.recipe_id, ingredient.ingredient, ingredient.ingredient)
  ), [isSpanish, translations])

  // ── load ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!prepMenuId || !currentCity) {
      setMenu(null); setNeeded(null); setLoading(false)
      return
    }
    let cancelled = false
    async function load() {
      setLoading(true)
      setLoadError(null)
      try {
        const menuData = await menusAPI.get(prepMenuId)
        if (cancelled) return
        setMenu(menuData)
      } catch (err) {
        if (!cancelled) { setLoadError(err.message); setLoading(false) }
        return
      }
      if (!cancelled) setLoading(false)
      // "Cut this much" comes from the Ordering tab's Needed (w/ Buffer) column.
      // Loaded separately so a slow ordering calculation never holds up the list.
      orderingAPI.calculate(prepMenuId)
        .then((data) => {
          if (cancelled) return
          const map = Object.fromEntries(groupedOrderingLookup(data))
          setNeeded(map)
        })
        .catch(() => { if (!cancelled) setNeeded({}) })
    }
    load()
    return () => { cancelled = true }
  }, [prepMenuId, currentCity])

  // ── dishes with weighed components ──────────────────────────────────────────
  const dishes = useMemo(() => {
    const all = menu?.dishes || []
    const order = [...MEAL_CATEGORIES, ...SIDE_CATEGORIES]
    return all
      // Sides (oats/chia/broth) are pre-portioned and not weighed at plating.
      // They're base-only, so now that 'base' is weighed they'd flood the list —
      // meals only. Mirrors the same exclusion on the Weights tab.
      .filter((d) => !SIDE_CATEGORIES.includes(d.category))
      .filter((d) => (d.components || []).some((c) => WEIGHED_TYPES.includes(c.component_type)))
      .sort((a, b) => order.indexOf(a.category) - order.indexOf(b.category))
  }, [menu])

  // ── core computation (mirrors the Weights tab — same store, same maths) ─────
  // need = Σ (prescribed portion × plate count) across every dish using it
  const ingredients = useMemo(() => buildIngredientGroups(dishes, inputs), [dishes, inputs])

  // Which recipe a row opens. `dish_components.recipe_id` is the real link, but a
  // component that was never linked would otherwise be a dead, untappable row —
  // and on a menu where few components are linked that reads as "nothing works".
  // So fall back to an exact name match against the recipe library (the same
  // name-based lookup the backend auto-matcher does; exact only, so a row can
  // never open the wrong recipe), and if there's still nothing, the row is STILL
  // tappable and the sheet says no recipe is linked yet.
  const recipeByName = useMemo(() => {
    const map = new Map()
    for (const r of recipes || []) {
      const n = (r.name || '').trim().toLowerCase()
      if (n && !map.has(n)) map.set(n, r.db_id || r.id)
    }
    return map
  }, [recipes])

  const recipeIdFor = useCallback((ing) => (
    ing.recipe_id || recipeByName.get((ing.ingredient || '').trim().toLowerCase()) || null
  ), [recipeByName])

  const ingredientList = useMemo(() => (
    [...ingredients.values()]
      .filter((ingredient) => !typeFilter || typeFilter.includes(ingredient.type))
      .filter((ingredient) => !ingredientFilter || ingredientFilter(ingredient))
      .sort(
      (a, b) => (TYPE_CONFIG[a.type]?.order ?? 99) - (TYPE_CONFIG[b.type]?.order ?? 99) || a.ingredient.localeCompare(b.ingredient)
    )
  ), [ingredients, typeFilter, ingredientFilter])

  // ── progress ────────────────────────────────────────────────────────────────
  // An item is complete when it's both cut AND weighed. Anything else is what's
  // still outstanding — the whole point of the page.
  const isCut = useCallback((key) => groupedInput(inputs, 'cut', ingredients.get(key) || { key }) === '1', [inputs, ingredients])
  const isWeighed = useCallback((key) => {
    const v = groupedInput(inputs, 'onHand', ingredients.get(key) || { key })
    return v !== undefined && v !== null && v !== ''
  }, [inputs, ingredients])

  // A weighed row is done when its cooked weight is in (which ticks it). A
  // check-only row (toppings) has no weight to enter — the tick IS the work.
  const isRowDone = useCallback((ing) => (
    isCheckOnly(ing.type) ? isCut(ing.key) : isCut(ing.key) && isWeighed(ing.key)
  ), [isCut, isWeighed])

  const { done, outstanding } = useMemo(() => {
    const out = []
    let d = 0
    for (const ing of ingredientList) {
      if (isRowDone(ing)) d += 1
      else out.push(ing)
    }
    return { done: d, outstanding: out }
  }, [ingredientList, isRowDone])

  const total = ingredientList.length
  const pct = total ? Math.round((done / total) * 100) : 0
  const allDone = total > 0 && done === total

  const alphabeticalItems = useMemo(() => (
    [...ingredientList].sort((a, b) => ingredientName(a).localeCompare(ingredientName(b)))
  ), [ingredientList, ingredientName])
  const incompleteItems = useMemo(() => alphabeticalItems.filter((item) => !isRowDone(item)), [alphabeticalItems, isRowDone])
  const completedItems = useMemo(() => alphabeticalItems.filter(isRowDone), [alphabeticalItems, isRowDone])
  const selectedIngredient = alphabeticalItems.find((item) => item.key === selectedKey) || incompleteItems[0] || completedItems[0] || null

  useEffect(() => {
    if (layout !== 'split' || !alphabeticalItems.length) return
    if (!selectedKey || !alphabeticalItems.some((item) => item.key === selectedKey)) {
      setSelectedKey((incompleteItems[0] || completedItems[0]).key)
    }
  }, [layout, alphabeticalItems, incompleteItems, completedItems, selectedKey])

  // Which rows have settled at the bottom. Recomputed on a debounce keyed off the
  // inputs, so a row only moves once entry has stopped — see SINK_DELAY_MS.
  const [sunk, setSunk] = useState(() => new Set())
  useEffect(() => {
    const id = setTimeout(
      () => setSunk(new Set(ingredientList.filter(isRowDone).map((i) => i.key))),
      SINK_DELAY_MS
    )
    return () => clearTimeout(id)
  }, [ingredientList, isRowDone])

  const [prepOpen, setPrepOpen] = useState(true)
  const wasAllDone = useRef(null)

  useEffect(() => {
    if (wasAllDone.current === allDone) return   // only act on the transition
    const first = wasAllDone.current === null
    wasAllDone.current = allDone
    if (first && !allDone) return                // initial render, still prepping
    setPrepOpen(!allDone)
  }, [allDone])

  // ── short-weight check ──────────────────────────────────────────────────────
  function checkShort(ing) {
    const raw = groupedInput(inputs, 'onHand', ing)
    if (raw === undefined || raw === null || raw === '') return
    if (!(ing.need > 0)) return
    if (warnedRef.current.has(ing.key)) return
    const weighed = Number(raw)
    if (Number.isNaN(weighed)) return
    if (weighed >= ing.need * SHORT_THRESHOLD) return
    warnedRef.current.add(ing.key)
    setShortAlert({
      key: ing.key,
      name: ingredientName(ing),
      need: ing.need,
      weighed,
      unit: ing.unit,
    })
  }

  // Entering a cooked weight IS the done signal — there's no useful state where
  // a row is ticked but has no weight, so the tick follows the weight both ways.
  // The checkbox stays tappable for the odd row someone finishes without weighing.
  function setWeight(ing, value) {
    setGroupedCell(setCell, 'onHand', ing, value)
    setGroupedCell(setCell, 'taskStatus', ing, 'in_progress')
    setGroupedCell(setCell, 'cut', ing, '')
  }

  function reenterWeight(key) {
    warnedRef.current.delete(key)   // still short after re-entry? warn again
    setShortAlert(null)
    const ingredient = ingredients.get(key) || { key }
    setGroupedCell(setCell, 'onHand', ingredient, '')
    setGroupedCell(setCell, 'cut', ingredient, '')
    requestAnimationFrame(() => weightInputs.current[key]?.focus())
  }

  const cityName = cities.find((c) => c.id === currentCity)?.name || currentCity?.toUpperCase()
  const hasInputs = ['onHand', 'unit', 'piece', 'cut'].some(
    (s) => Object.values(inputs[s] || {}).some((v) => v !== undefined && v !== '')
  )

  // ── states ──────────────────────────────────────────────────────────────────
  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-havn-green-700 animate-spin" /></div>
  }
  if (!prepMenuId || loadError) {
    return (
      <div className="bg-white rounded-xl border border-havn-cream-300 p-10 text-center">
        <AlertCircle className="w-8 h-8 text-havn-gray-400 mx-auto mb-3" />
        <h3 className="font-display text-lg font-semibold text-havn-charcoal mb-1">{t('No menu for this week')}</h3>
        <p className="text-sm text-havn-gray-500">{loadError || t("Once this week's menu exists, the prep list builds itself.")}</p>
      </div>
    )
  }

  // Derived from WEIGHED_TYPES rather than a hard-coded list, so a type added
  // there (e.g. base/Topping for salad toppings) shows up here automatically
  // instead of silently dropping its rows off the prep list.
  const groups = [...WEIGHED_TYPES]
    .filter((type) => !typeFilter || typeFilter.includes(type))
    .sort((a, b) => {
      const aChef = a === 'protein' || a === 'carb' ? 1 : 0
      const bChef = b === 'protein' || b === 'carb' ? 1 : 0
      return aChef - bChef || (TYPE_CONFIG[a]?.order ?? 99) - (TYPE_CONFIG[b]?.order ?? 99)
    })
    .map((type) => ({
      type,
      // Outstanding work first, finished rows parked underneath it. Still visible
      // and still editable — just out of the way of what's left to do.
      items: ingredientList
        .filter((i) => i.type === type)
        .sort((a, b) => (sunk.has(a.key) ? 1 : 0) - (sunk.has(b.key) ? 1 : 0)),
    }))
    .filter((g) => g.items.length)

  if (layout === 'split') {
    return (
      <PrepSplitWorkspace
        menu={menu}
        cityName={cityName}
        done={done}
        total={total}
        pct={pct}
        incompleteItems={incompleteItems}
        completedItems={completedItems}
        selected={selectedIngredient}
        selectedKey={selectedIngredient?.key || null}
        onSelect={setSelectedKey}
        doneOpen={doneOpen}
        onToggleDone={() => setDoneOpen((open) => !open)}
        ingredientName={ingredientName}
        isRowDone={isRowDone}
        isCut={isCut}
        isCheckOnly={isCheckOnly}
        inputs={inputs}
        needed={needed}
        stateFor={stateFor}
        failedCount={failedCount}
        retryFailed={retryFailed}
        hasInputs={hasInputs}
        clearAll={clearAll}
        onToggleCut={(ingredient) => { setGroupedCell(setCell, 'taskStatus', ingredient, isCut(ingredient.key) ? 'not_started' : 'complete'); setGroupedCell(setCell, 'cut', ingredient, isCut(ingredient.key) ? '' : '1') }}
        onWeight={setWeight}
        onWeightBlur={checkShort}
        onOpenRecipe={(ingredient) => setRecipeSheet({ recipeId: recipeIdFor(ingredient), name: ingredientName(ingredient) })}
        inputRef={(key, element) => { weightInputs.current[key] = element }}
        t={t}
      >
        {shortAlert && <ShortWeightModal alert={shortAlert} onDismiss={() => setShortAlert(null)} onReenter={() => reenterWeight(shortAlert.key)} t={t} />}
        {recipeSheet && <RecipeSheetModal menuId={prepMenuId} recipeId={recipeSheet.recipeId} fallbackName={recipeSheet.name} isSpanish={isSpanish} translations={translations} onClose={() => setRecipeSheet(null)} />}
      </PrepSplitWorkspace>
    )
  }

  return (
    <div className="pb-10">
      <style>{`
        @media print {
          @page { size: letter; margin: 0.5in; }
          body { background: white !important; }
          aside, header, .floating-tools, .print-hide { display: none !important; }
          main { padding: 0 !important; overflow: visible !important; }
          .prep-sticky { position: static !important; }
          .weights-card, .prep-row { break-inside: avoid; page-break-inside: avoid; box-shadow: none !important; }
          input { border: 0 !important; }
        }
      `}</style>

      {/* ── STATUS ────────────────────────────────────────────────────────────
          The first thing a lead should see walking up to the iPad, and it has to
          survive scrolling — the list is long and the status is the point. */}
      <div className="prep-sticky rounded-xl border border-havn-cream-300 bg-white px-4 lg:px-5 py-4">
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <h1 className="font-display text-xl lg:text-2xl font-semibold text-havn-green-900">
            {t('Prep Manager')} <span className="text-havn-gray-400 font-normal">·</span> {menu?.name} <span className="text-havn-gray-400 font-normal">·</span> {cityName}
          </h1>
          <div className="flex items-center gap-3">
            <span className="font-display text-lg font-bold text-havn-green-900 tabular-nums">
              {done} / {total}
            </span>
            <span className="text-sm text-havn-gray-500">{t('complete')}</span>
          </div>
        </div>

        <div className="mt-2 h-4 w-full rounded-full bg-havn-cream-300 overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
          <div
            className={`h-full rounded-full transition-all duration-300 ${done === total && total > 0 ? 'bg-havn-green-600' : 'bg-havn-green-700'}`}
            style={{ width: `${pct}%` }}
          />
        </div>

        {total > 0 && (
          outstanding.length === 0 ? (
            <p className="mt-2 text-sm font-medium text-havn-green-700">{t('All prep done — plate it.')}</p>
          ) : (
            <p className="mt-2 text-sm text-havn-gray-600">
              <span className="text-havn-gray-500">{t('Waiting on')}: </span>
              <span className="font-medium text-havn-charcoal">
                {outstanding.slice(0, 6).map(ingredientName).join(', ')}
                {outstanding.length > 6 ? ` +${outstanding.length - 6}` : ''}
              </span>
            </p>
          )
        )}

        {failedCount > 0 && (
          <div className="print-hide mt-2 flex items-center gap-3 px-3 py-2 rounded-lg bg-red-50 border border-red-200">
            <CloudOff className="w-4 h-4 text-red-600 flex-shrink-0" />
            <span className="text-sm text-red-700 flex-1">
              {failedCount} {failedCount === 1 ? t('entry not saved — retrying') : t('entries not saved — retrying')}
            </span>
            <button onClick={retryFailed} className="min-h-[36px] px-3 py-1.5 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-lg hover:bg-red-50">
              {t('Retry now')}
            </button>
          </div>
        )}
      </div>

      {/* ── TOOLBAR ───────────────────────────────────────────────────────────── */}
      <div className="print-hide flex items-center justify-end gap-2 mt-4">
        {hasInputs && (
          <button onClick={clearAll} className="inline-flex items-center gap-2 min-h-[44px] px-4 py-2 bg-white border border-havn-cream-300 text-havn-gray-600 hover:bg-havn-cream-50 rounded-lg font-medium text-sm transition-colors">
            <RotateCcw className="w-4 h-4" /> {t('Clear inputs')}
          </button>
        )}
        <button onClick={() => window.print()} className="inline-flex items-center gap-2 min-h-[44px] px-4 py-2 bg-havn-green-700 hover:bg-havn-green-800 text-white rounded-lg font-medium text-sm transition-colors shadow-soft">
          <Printer className="w-4 h-4" /> {t('Print')}
        </button>
      </div>

      {total === 0 ? (
        <div className="bg-white rounded-xl border border-havn-cream-300 p-10 text-center mt-4">
          <AlertCircle className="w-8 h-8 text-havn-gray-400 mx-auto mb-3" />
          <h3 className="font-display text-lg font-semibold text-havn-charcoal mb-1">{t('Nothing to prep yet')}</h3>
          <p className="text-sm text-havn-gray-500">{t('No dishes on this menu have protein, carb, or vegetable components.')}</p>
        </div>
      ) : (
        <div className="space-y-6 mt-4">
          {/* Prep done → the list folds itself out of the way behind this bar.
              Still one tap open, and always expanded on paper. */}
          {allDone && (
            <button
              onClick={() => setPrepOpen((o) => !o)}
              aria-expanded={prepOpen}
              className="print-hide w-full min-h-[56px] flex items-center gap-3 px-5 py-3 bg-white border border-havn-cream-300 rounded-xl hover:bg-havn-cream-50 transition-colors"
            >
              <Check className="w-5 h-5 text-havn-green-700" strokeWidth={3} />
              <h2 className="font-display text-lg font-semibold text-havn-green-900">{t('Prep list')}</h2>
              <span className="text-xs text-havn-gray-400 tabular-nums">· {done} / {total}</span>
              <ChevronDown className={`w-5 h-5 text-havn-gray-400 ml-auto transition-transform ${prepOpen ? 'rotate-180' : ''}`} />
            </button>
          )}

          <div className={`space-y-6 ${allDone && !prepOpen ? 'hidden print:block print:space-y-6' : ''}`}>
            {groups.map(({ type, items }) => {
              const tc = TYPE_CONFIG[type]
              const checkOnly = isCheckOnly(type)
              // Responsibility follows the real kitchen handoff: raw proteins,
              // marinades and starches are chef work. The shared iPad is still
              // operated by the kitchen team, who check those tasks off for him.
              const responsibility = type === 'protein' || type === 'carb'
                ? t('Chef work — kitchen team checks off')
                : t('Prep team')
              return (
                <section key={type} className="weights-card bg-white rounded-xl border border-havn-cream-300 overflow-hidden">
                  <div className="px-4 py-3 border-b border-havn-cream-200 bg-havn-cream-50 flex items-center gap-2 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <strong className="block text-sm text-havn-charcoal">{responsibility}</strong>
                      <span className={`inline-flex mt-1 px-2.5 py-0.5 text-xs rounded-full border ${tc.color}`}>{t(tc.label)}</span>
                    </div>
                    <span className="text-xs text-havn-gray-400 tabular-nums">{items.filter(isRowDone).length} / {items.length}</span>
                    {checkOnly && (
                      <span className="text-xs text-havn-gray-400">{t('Tick when done — nothing to weigh')}</span>
                    )}
                  </div>
                  <div>
                    {items.map((ing) => (
                      <PrepRow
                        key={ing.key}
                        ing={ing}
                        displayName={ingredientName(ing)}
                        checkOnly={checkOnly}
                        cut={isCut(ing.key)}
                        weight={groupedInput(inputs, 'onHand', ing) ?? ''}
                        orderRow={needed ? (needed[ing.key] || (ing.recipe_id ? needed[`recipe::${ing.recipe_id}`] : undefined)) : undefined}
                        neededLoading={needed === null}
                        saveState={groupedSaveState(stateFor, inputs, 'onHand', ing)}
                        cutState={groupedSaveState(stateFor, inputs, 'cut', ing)}
                        onToggleCut={() => { setGroupedCell(setCell, 'taskStatus', ing, isCut(ing.key) ? 'not_started' : 'complete'); setGroupedCell(setCell, 'cut', ing, isCut(ing.key) ? '' : '1') }}
                        onWeight={(v) => setWeight(ing, v)}
                        onWeightBlur={() => checkShort(ing)}
                        onOpenRecipe={() => setRecipeSheet({ recipeId: recipeIdFor(ing), name: ingredientName(ing) })}
                        inputRef={(el) => { weightInputs.current[ing.key] = el }}
                        t={t}
                      />
                    ))}
                  </div>
                </section>
              )
            })}
          </div>

        </div>
      )}

      {shortAlert && (
        <ShortWeightModal
          alert={shortAlert}
          onDismiss={() => setShortAlert(null)}
          onReenter={() => reenterWeight(shortAlert.key)}
          t={t}
        />
      )}

      {recipeSheet && (
        <RecipeSheetModal
          recipeId={recipeSheet.recipeId}
          fallbackName={recipeSheet.name}
          isSpanish={isSpanish}
          translations={translations}
          onClose={() => setRecipeSheet(null)}
        />
      )}
    </div>
  )
}

function PrepSplitWorkspace({
  menu, cityName, done, total, pct, incompleteItems, completedItems, selected, selectedKey,
  onSelect, doneOpen, onToggleDone, ingredientName, isRowDone, isCut, isCheckOnly,
  inputs, needed, stateFor, failedCount, retryFailed, hasInputs, clearAll, onToggleCut,
  onWeight, onWeightBlur, onOpenRecipe, inputRef, t, children,
}) {
  const selectedName = selected ? ingredientName(selected) : ''
  const selectedDone = selected ? isRowDone(selected) : false
  const checkOnly = selected ? isCheckOnly(selected.type) : false
  const orderRow = selected ? needed?.[selected.key] : null
  const rawAmount = orderRow
    ? formatQuantity(convertFromLbs(orderRow.needed_with_buffer, orderRow.display_unit))
    : null
  const rawUnit = orderRow ? getUnitLabel(orderRow.display_unit || orderRow.unit) : ''

  const queueButton = (ingredient, complete = false) => {
    const name = ingredientName(ingredient)
    const active = ingredient.key === selectedKey
    return (
      <button
        key={ingredient.key}
        onClick={() => onSelect(ingredient.key)}
        className={`w-full min-h-[50px] px-3 py-2.5 flex items-center gap-3 text-left border-b border-havn-cream-200 last:border-b-0 transition-colors ${active ? 'bg-havn-brass-50 border-l-4 border-l-havn-brass-500' : 'bg-white hover:bg-havn-cream-50'}`}
      >
        <span className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${complete ? 'bg-havn-green-700 text-white' : 'border-2 border-havn-cream-300 text-transparent'}`}><Check className="w-4 h-4" /></span>
        <span className="min-w-0 flex-1"><strong className={`block text-sm truncate ${complete ? 'text-havn-gray-500' : 'text-havn-charcoal'}`}>{name}</strong><span className="text-[10px] uppercase tracking-wider text-havn-gray-400">{t(TYPE_CONFIG[ingredient.type]?.label || ingredient.type)}</span></span>
        <ChevronRight className="w-4 h-4 text-havn-gray-300" />
      </button>
    )
  }

  return (
    <div className="space-y-3">
      {failedCount > 0 && <div className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700"><CloudOff className="w-4 h-4" /><span className="flex-1">{failedCount} {t('entry not saved — retrying')}</span><button onClick={retryFailed} className="btn-secondary">{t('Retry now')}</button></div>}

      <div className="h-[calc(100dvh-250px)] min-h-[500px] grid grid-cols-[minmax(280px,36%)_minmax(0,1fr)] rounded-2xl border border-havn-cream-300 bg-white overflow-hidden shadow-soft">
        <aside className="min-h-0 border-r border-havn-cream-300 flex flex-col bg-havn-cream-50/50">
          <div className="p-4 border-b border-havn-cream-300 bg-white">
            <div className="flex items-center justify-between gap-3"><div><p className="text-xs uppercase tracking-wider text-havn-gray-400">{menu?.name} · {cityName}</p><h2 className="font-display text-xl text-havn-green-900">{t('Friday prep')}</h2></div><strong className="font-display text-xl text-havn-green-900 tabular-nums">{done}/{total}</strong></div>
            <div className="mt-3 h-2.5 rounded-full bg-havn-cream-300 overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}><div className="h-full bg-havn-green-700 rounded-full transition-all" style={{ width: `${pct}%` }} /></div>
            <div className="mt-3 flex items-center justify-between gap-2"><span className="text-xs font-medium text-havn-charcoal">{incompleteItems.length} {t('remaining')}</span></div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            {incompleteItems.length ? incompleteItems.map((item) => queueButton(item)) : <p className="p-5 text-sm text-havn-green-700 font-medium">{t('All prep done — plate it.')}</p>}
            {doneOpen && completedItems.map((item) => queueButton(item, true))}
          </div>
          <button onClick={onToggleDone} aria-expanded={doneOpen} className="w-full min-h-[48px] px-4 flex items-center gap-2 bg-havn-green-800 border-t border-havn-green-900 text-left text-white flex-shrink-0"><Check className="w-4 h-4 text-havn-brass-300" /><strong className="text-sm">{t('Done')}</strong><span className="text-xs text-white/70">({completedItems.length})</span><ChevronDown className={`w-4 h-4 ml-auto text-white/70 transition-transform ${doneOpen ? 'rotate-180' : ''}`} /></button>
        </aside>

        <section className="min-h-0 overflow-y-auto p-5 lg:p-6">
          {selected ? (
            <div className="max-w-3xl mx-auto">
              <div className="flex items-start justify-between gap-4 border-b border-havn-cream-200 pb-5">
                <div className="min-w-0"><span className={`inline-flex px-2.5 py-1 text-xs rounded-full border ${TYPE_CONFIG[selected.type]?.color || ''}`}>{t(TYPE_CONFIG[selected.type]?.label || selected.type)}</span><h2 className="font-display text-3xl text-havn-green-900 mt-3">{selectedName}</h2><p className="text-sm text-havn-gray-500 mt-1">{selectedDone ? t('Completed — available in Done') : t('Select the task, enter the cooked weight, and confirm completion.')}</p></div>
                <button role="checkbox" aria-checked={isCut(selected.key)} onClick={() => onToggleCut(selected)} className={`w-16 h-16 rounded-2xl border-2 flex items-center justify-center flex-shrink-0 ${isCut(selected.key) ? 'bg-havn-green-700 border-havn-green-700 text-white' : 'bg-white border-havn-cream-300 text-transparent'}`}><Check className="w-9 h-9" strokeWidth={3} /></button>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-5">
                <div className="rounded-xl border border-havn-cream-300 bg-havn-cream-50 p-4"><span className="text-xs uppercase tracking-wider text-havn-gray-400">{t('Raw needed')}</span>{needed === null ? <span className="block mt-2 h-8 rounded bg-havn-cream-200 animate-pulse" /> : <strong className="font-display text-3xl text-havn-green-900 block mt-2 tabular-nums">{rawAmount ?? '—'} <span className="text-base text-havn-gray-500">{rawUnit}</span></strong>}</div>
                <div className="rounded-xl border border-havn-cream-300 bg-white p-4"><span className="text-xs uppercase tracking-wider text-havn-gray-400">{checkOnly ? t('Completion') : t('Cooked weight')}</span>{checkOnly ? <button onClick={() => onToggleCut(selected)} className={`w-full min-h-[48px] mt-2 rounded-xl font-medium ${isCut(selected.key) ? 'bg-havn-green-100 text-havn-green-800' : 'bg-havn-green-700 text-white'}`}>{isCut(selected.key) ? t('Mark open') : t('Mark complete')}</button> : <div className="mt-2 flex items-center gap-2"><input ref={(element) => inputRef(selected.key, element)} type="number" min="0" inputMode="decimal" value={groupedInput(inputs, 'onHand', selected) ?? ''} onChange={(event) => onWeight(selected, event.target.value)} onBlur={() => onWeightBlur(selected)} className="min-w-0 w-full h-12 px-3 text-right rounded-xl border border-havn-cream-300 font-display text-2xl text-havn-green-900 focus:outline-none focus:ring-2 focus:ring-havn-green-600" /><span className="text-sm text-havn-gray-500">{selected.unit}</span></div>}<div className="mt-2"><SaveState state={checkOnly ? groupedSaveState(stateFor, inputs, 'cut', selected) : groupedSaveState(stateFor, inputs, 'onHand', selected)} t={t} /></div></div>
              </div>

              <button onClick={() => onOpenRecipe(selected)} className="mt-5 w-full min-h-[58px] rounded-xl border border-havn-green-200 bg-havn-green-50 hover:bg-havn-green-100 px-4 flex items-center gap-3 text-left"><BookOpen className="w-6 h-6 text-havn-green-700" /><span><strong className="block text-havn-green-900">{t('Open recipe')}</strong><span className="text-sm text-havn-gray-500">{selectedName}</span></span><ChevronRight className="w-5 h-5 text-havn-green-600 ml-auto" /></button>
            </div>
          ) : <div className="h-full flex items-center justify-center text-havn-gray-500">{t('Nothing to prep yet')}</div>}
        </section>
      </div>
      {children}
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// ROW
// ══════════════════════════════════════════════════════════════════════════════

function PrepRow({
  ing, displayName, checkOnly, cut, weight, orderRow, neededLoading, saveState, cutState,
  onToggleCut, onWeight, onWeightBlur, onOpenRecipe, inputRef, t,
}) {
  const weighed = weight !== '' && weight !== null && weight !== undefined
  const complete = checkOnly ? cut : cut && weighed

  // "Cut this much" — the Ordering Needed (w/ Buffer) figure, shown exactly as
  // the Ordering tab shows it. Not recomputed, and never the cooked figure.
  let cutQty = null
  let cutUnit = ''
  if (orderRow) {
    cutQty = formatQuantity(convertFromLbs(orderRow.needed_with_buffer, orderRow.display_unit))
    cutUnit = getUnitLabel(orderRow.display_unit || orderRow.unit)
  }

  return (
    <div className={`prep-row flex items-start gap-3 sm:gap-4 px-3 sm:px-4 py-3 border-b border-havn-cream-100 last:border-b-0 transition-colors ${complete ? 'bg-havn-green-50/40' : ''}`}>
      {/* Checkbox — the primary tap target, sized for gloves */}
      <button
        role="checkbox"
        aria-checked={cut}
        aria-label={`${t('Done')} — ${displayName}`}
        onClick={onToggleCut}
        className={`flex-shrink-0 w-12 h-12 rounded-xl border-2 flex items-center justify-center transition-colors
          ${cut ? 'bg-havn-green-700 border-havn-green-700 text-white' : 'bg-white border-havn-cream-300 text-transparent hover:border-havn-green-600'}`}
      >
        <Check className="w-7 h-7" strokeWidth={3} />
        {cutState === 'failed' && <span className="sr-only">{t('not saved')}</span>}
      </button>

      {/* Name — always tappable, always opens the recipe sheet. Never a dead row:
          if nothing is linked the sheet says so, which is information the cook (or
          whoever links recipes) can act on. */}
      <div className={`flex-1 min-w-0 ${complete ? 'opacity-60' : ''}`}>
        <button
          onClick={onOpenRecipe}
          aria-label={`${displayName} — ${t('Recipe')}`}
          className="w-full text-left group flex items-start gap-2 min-h-[48px] py-1 pr-2 rounded-lg hover:bg-havn-cream-50 active:bg-havn-cream-100 transition-colors"
        >
          <span className="text-lg font-medium text-havn-charcoal leading-snug group-hover:text-havn-green-800 underline decoration-havn-cream-300 underline-offset-4 decoration-2">
            {displayName}
          </span>
          <BookOpen className="w-4 h-4 text-havn-gray-400 flex-shrink-0 mt-1.5 group-hover:text-havn-green-700" />
        </button>
      </div>

      {/* A topping has no raw figure to prep to and no cooked weight to record —
          the tick is the whole row. Showing empty weight fields on it would just
          be something for a cook to wonder about. */}
      {checkOnly ? (
        <div className="flex items-center flex-shrink-0 self-center">
          <span className={`text-sm ${cut ? 'text-havn-green-700 font-medium' : 'text-havn-gray-400'}`}>
            {cut ? t('Done') : t('Tick when done')}
          </span>
          <SaveState state={cutState} t={t} />
        </div>
      ) : (
      <div className="flex flex-col items-end gap-2 flex-shrink-0">
        <div className="flex items-baseline gap-1.5 whitespace-nowrap">
          <span className="text-[11px] uppercase tracking-widest text-havn-gray-400">{t('Raw weight')}</span>
          {neededLoading ? (
            <span className="inline-block w-16 h-6 rounded bg-havn-cream-200 animate-pulse" />
          ) : cutQty != null ? (
            <>
              <span className="font-display text-2xl font-bold text-havn-green-900 tabular-nums">{cutQty}</span>
              <span className="text-sm text-havn-gray-500">{cutUnit}</span>
            </>
          ) : (
            <span className="text-sm text-havn-gray-300">—</span>
          )}
        </div>

        <div className="flex items-center gap-2 whitespace-nowrap">
          <span className="text-[11px] uppercase tracking-widest text-havn-gray-400 hidden sm:inline">{t('Cooked weight')}</span>
          <input
            ref={inputRef}
            type="number"
            min="0"
            inputMode="decimal"
            value={weight}
            placeholder="0"
            onChange={(e) => onWeight(e.target.value)}
            onBlur={onWeightBlur}
            aria-label={`${t('Cooked weight')} — ${displayName}`}
            className="w-24 h-11 px-2 text-right tabular-nums font-display text-lg font-semibold text-havn-green-900 bg-white border border-havn-cream-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-havn-green-600"
          />
          <span className="text-xs text-havn-gray-400 w-6 text-left">{ing.unit}</span>
          <SaveState state={saveState} t={t} />
        </div>
      </div>
      )}
    </div>
  )
}

// Per-cell save state. A weigh-in that never reached the server used to look
// exactly like one that did — this is the difference.
function SaveState({ state, t }) {
  const base = 'print-hide text-[11px] inline-flex items-center gap-1 w-[74px]'
  if (state === 'pending') {
    return <span className={`${base} text-havn-gray-400`}><Loader2 className="w-3.5 h-3.5 animate-spin" />{t('saving')}</span>
  }
  if (state === 'failed') {
    return <span className={`${base} text-red-600 font-medium`}><CloudOff className="w-3.5 h-3.5" />{t('not saved')}</span>
  }
  if (state === 'saved') {
    return <span className={`${base} text-havn-green-700`}><Check className="w-3.5 h-3.5" />{t('saved')}</span>
  }
  return <span className={base} aria-hidden="true" />
}

// ══════════════════════════════════════════════════════════════════════════════
// SHORT-WEIGHT MODAL
// ══════════════════════════════════════════════════════════════════════════════
//
// Fires when the cooked weight is more than 30% under what the recipes need.
// A modal, not a toast — it has to be acknowledged. "Notify manager" is a
// MESSAGE, not a feature: nothing is sent anywhere, the cook goes and tells them.

function ShortWeightModal({ alert, onDismiss, onReenter, t }) {
  const shortPct = alert.need > 0 ? Math.round((1 - alert.weighed / alert.need) * 100) : 0
  const fmt = (v) => (alert.unit === 'ea' ? fmtPieces(v) : `${round1(v)} lbs`)

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onDismiss() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onDismiss])

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/50 flex items-center justify-center p-4"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="short-title"
    >
      <div className="bg-white rounded-2xl shadow-elevated w-full max-w-md">
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 id="short-title" className="font-display text-xl font-semibold text-havn-charcoal">
                {alert.name} {t('is short')}
              </h3>

              <dl className="mt-4 space-y-1.5">
                <div className="flex items-baseline justify-between gap-4">
                  <dt className="text-sm text-havn-gray-500">{t('Needed')}</dt>
                  <dd className="font-display text-lg font-semibold text-havn-charcoal tabular-nums">
                    {fmt(alert.need)} <span className="text-sm font-normal text-havn-gray-500">{t('cooked')}</span>
                  </dd>
                </div>
                <div className="flex items-baseline justify-between gap-4">
                  <dt className="text-sm text-havn-gray-500">{t('Weighed')}</dt>
                  <dd className="font-display text-lg font-semibold text-red-600 tabular-nums">
                    {fmt(alert.weighed)} <span className="text-sm font-normal text-red-500">— {shortPct}% {t('short')}</span>
                  </dd>
                </div>
              </dl>

              <p className="mt-4 text-base font-medium text-havn-charcoal">
                {t('Go tell the manager about this.')}
              </p>
            </div>
          </div>

          <div className="mt-6 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
            {/* Most likely cause is a typo or a partial batch, so re-entering is
                a first-class option, not a hidden one. */}
            <button
              onClick={onReenter}
              className="min-h-[48px] px-5 py-3 bg-white border border-havn-cream-300 text-havn-gray-700 hover:bg-havn-cream-50 rounded-xl font-medium transition-colors"
            >
              {t('Re-enter weight')}
            </button>
            <button
              onClick={onDismiss}
              autoFocus
              className="min-h-[48px] px-6 py-3 bg-havn-green-700 hover:bg-havn-green-800 text-white rounded-xl font-medium transition-colors"
            >
              {t('Got it')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
